import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { useConfigurationStore } from '@store/use-configuration-store';
import { useElearningWpRemoteStore } from '@store/use-ewr-store';

import { ROUTES } from '../constants/routes';
import type { ApiError } from '../types';
import { apis } from './api/Api';

export type {
  ElearningWpRemoteTestAnswer,
  ElearningWpRemoteTestQuestion,
  ElearningWpRemoteTestSubmitResponse
} from './elearning-wp-remote';

function notifyError(err: ApiError) {
  notification.error({
    title: err?.response?.data?.errorMessage ?? 'Something went wrong',
    placement: 'topRight'
  });
}

export const useElearningWpOnSiteMveNumber = () => {
  const { friendly_url } = useConfigurationStore();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (body: { mveNumber: string }) =>
      apis.elearningWpOnSite.mveNumber(friendly_url, body),
    onSuccess: () => navigate(ROUTES.EWOS_DOB),
    onError: notifyError
  });
};

export const useElearningWpOnSiteBirthDate = () => {
  const { friendly_url } = useConfigurationStore();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (body: { birthDate: string }) =>
      apis.elearningWpOnSite.birthDate(friendly_url, body),
    onSuccess: () => navigate(ROUTES.EWOS_WELCOME),
    onError: notifyError
  });
};

export const useElearningWpOnSitePersonInfoQuery = (friendlyUrl: string) =>
  useQuery({
    queryKey: ['ewos-person-info', friendlyUrl],
    queryFn: () =>
      apis.elearningWpOnSite.getPersonInfo(friendlyUrl).then((r) => r.data),
    enabled: !!friendlyUrl,
    refetchOnMount: 'always'
  });

export const useElearningWpOnSiteUpdatePersonMutation = (
  friendlyUrl: string,
  contactId: string | null
) =>
  useMutation({
    mutationFn: (body: unknown) =>
      apis.elearningWpOnSite.updatePerson(
        friendlyUrl,
        contactId as string,
        body
      ),
    onError: notifyError
  });

export const useElearningWpOnSiteDataProtectionDeclineMutation = (
  friendlyUrl: string,
  contactId: string | null
) =>
  useMutation({
    mutationFn: () =>
      apis.elearningWpOnSite.dataProtectionDecline(
        friendlyUrl,
        contactId as string,
        {
          decline: true
        }
      ),
    onError: notifyError
  });

export const useElearningWpOnSiteElearningAccept = () => {
  const { friendly_url } = useConfigurationStore();
  const { ewrContactId } = useElearningWpRemoteStore();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accepted: boolean) =>
      apis.elearningWpOnSite.elearningAccept(
        friendly_url,
        ewrContactId as string,
        {
          elearningAccept: accepted
        }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['ewos-test', friendly_url, ewrContactId]
      });
    },
    onError: notifyError
  });
};

export const useElearningWpOnSiteFinish = () => {
  const { friendly_url } = useConfigurationStore();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => apis.elearningWpOnSite.finish(friendly_url),
    onSuccess: () => navigate(ROUTES.EWOS_FINAL),
    onError: notifyError
  });
};

export const useElearningWpOnSiteTestQuery = () => {
  const { friendly_url } = useConfigurationStore();
  const { ewrContactId } = useElearningWpRemoteStore();
  return useQuery({
    queryKey: ['ewos-test', friendly_url, ewrContactId],
    queryFn: () =>
      apis.elearningWpOnSite
        .getElearningTest(friendly_url, ewrContactId as string)
        .then((r) => r.data.testQuestions),
    enabled: !!ewrContactId
  });
};

interface SubmitTestBody {
  testAnswers: { questionId: number; answerToken: string }[];
}

export const useElearningWpOnSiteSubmitTest = () => {
  const { friendly_url } = useConfigurationStore();
  const { ewrContactId } = useElearningWpRemoteStore();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitTestBody) =>
      apis.elearningWpOnSite
        .submitElearningTest(friendly_url, ewrContactId as string, body)
        .then((r) => r.data),
    onSuccess: async (result: { status: string }) => {
      if (result.status === 'fail') {
        await queryClient.invalidateQueries({
          queryKey: ['ewos-test', friendly_url, ewrContactId]
        });
      }
    },
    onError: notifyError
  });
};
